import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Building2, User, Mail, Lock, ChevronRight, Check, Car, Package, UserCircle, ParkingCircle, Layers, Handshake, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function RegisterPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const preselectedPlan = location.state?.plan || 'basic';
    const urlParams = new URLSearchParams(location.search);
    const preselectedEdition = location.state?.edition || urlParams.get('edition') || 'carrier';
    
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        companyName: '',
        name: '',
        email: urlParams.get('email') || '',
        password: '',
        confirmPassword: '',
        plan: preselectedPlan,
        edition: preselectedEdition,
        leasingCompanyId: 'none'
    });
    const [leasingCompanies, setLeasingCompanies] = useState([]);

    useEffect(() => {
        api.get('/leasing/companies').then((res) => setLeasingCompanies(res.data)).catch(() => setLeasingCompanies([]));
    }, []);

    // Edition-specific pricing
    const editionPricing = {
        personal: { basic: 2.5, professional: 2.5, enterprise: 2.5 },
        inspection: { basic: 6, professional: 5, enterprise: 4 },
        carrier: { basic: 9, professional: 7, enterprise: 5 },
        hybrid: { basic: 9, professional: 7, enterprise: 5 },
        leasing: { basic: 6, professional: 5, enterprise: 4 }
    };

    // Personal Edition has simplified plans
    const personalPlans = [
        { value: 'basic', label: 'Personal Use', vehicleLimit: 3, pricePerVehicle: 2.5, recommended: true }
    ];

    const businessPlans = [
        { value: 'basic', label: 'Basic', vehicleLimit: 7, recommended: true },
        { value: 'professional', label: 'Professional', vehicleLimit: 15 },
        { value: 'enterprise', label: 'Enterprise', vehicleLimit: 30 },
        { value: 'custom', label: 'Custom', vehicleLimit: null, pricePerVehicle: null }
    ].map(plan => ({
        ...plan,
        pricePerVehicle: plan.value === 'custom' ? null : editionPricing[formData.edition]?.[plan.value]
    }));

    const workshopPlans = [{ value: 'basic', label: 'Συνεργείο', vehicleLimit: null, pricePerVehicle: null }];
    const plans = formData.edition === 'personal' ? personalPlans : formData.edition === 'workshop' ? workshopPlans : businessPlans;

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const validateStep1 = () => {
        if (!formData.companyName.trim()) {
            toast.error('Please enter your company name');
            return false;
        }
        if (formData.companyName.length < 2) {
            toast.error('Company name must be at least 2 characters');
            return false;
        }
        return true;
    };

    const validateStep2 = () => {
        if (!formData.name.trim()) {
            toast.error('Please enter your name');
            return false;
        }
        if (!formData.email.trim()) {
            toast.error('Please enter your email');
            return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            toast.error('Please enter a valid email');
            return false;
        }
        if (!formData.password) {
            toast.error('Please enter a password');
            return false;
        }
        if (formData.password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return false;
        }
        if (formData.password !== formData.confirmPassword) {
            toast.error('Passwords do not match');
            return false;
        }
        return true;
    };

    const handleNextStep = () => {
        if (step === 1 && validateStep1()) {
            setStep(2);
        } else if (step === 2 && validateStep2()) {
            setStep(3);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const response = await api.post('/auth/register-company', {
                company_name: formData.companyName.trim(),
                name: formData.name.trim(),
                email: formData.email.trim().toLowerCase(),
                password: formData.password,
                plan: formData.plan,
                edition: formData.edition,
                billing_cycle: 'monthly',
                leasing_company_id: formData.edition !== 'leasing' && formData.leasingCompanyId !== 'none' ? formData.leasingCompanyId : null
            });

            toast.success(formData.edition === 'workshop' ? 'Ο λογαριασμός συνεργείου δημιουργήθηκε!' : 'Account created successfully! Starting your 14-day free trial.');
            
            // Auto-login after registration
            const loginResponse = await api.post('/auth/login', {
                email: formData.email.trim().toLowerCase(),
                password: formData.password
            });

            localStorage.setItem('fleet_token', loginResponse.data.access_token);
            localStorage.setItem('user', JSON.stringify(loginResponse.data.user));
            
            // Force reload to update AuthContext with new token
            window.location.href = '/app/dashboard';
        } catch (error) {
            console.error('Registration error:', error);
            toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const selectedPlan = plans.find(p => p.value === formData.plan);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-[#1B7EB5]/5 flex">
            {/* Left Side - Form */}
            <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-md">
                    {/* Logo */}
                    <div className="flex items-center mb-8">
                        <img 
                            src="https://customer-assets-agu9un31.emergentagent.net/job_fleet-ops-62/artifacts/sxi4lya9_%CE%91%CE%BD%CF%89%CC%81%CE%BD%CF%85%CE%BC%CE%BF%20%CF%83%CF%87%CE%B5%CC%81%CE%B4%CE%B9%CE%BF.webp" 
                            alt="Cofleet Logo" 
                            style={{ width: '200px', height: 'auto' }}
                            className="object-contain"
                        />
                    </div>

                    {/* Progress Steps */}
                    <div className="flex items-center gap-2 mb-8">
                        {[1, 2, 3].map((s) => (
                            <div key={s} className="flex items-center">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                    step >= s 
                                        ? 'bg-[#1B7EB5] text-white' 
                                        : 'bg-slate-200 text-slate-500'
                                }`}>
                                    {step > s ? <Check className="w-4 h-4" /> : s}
                                </div>
                                {s < 3 && (
                                    <div className={`w-12 h-1 mx-2 ${step > s ? 'bg-[#1B7EB5]' : 'bg-slate-200'}`} />
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Step 1: Company Info & Edition Selection */}
                    {step === 1 && (
                        <div className="space-y-6" data-testid="register-step-1">
                            <div>
                                <h1 className="text-2xl font-bold text-slate-800 mb-2">Create your account</h1>
                                <p className="text-slate-500">Start with your information</p>
                            </div>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="companyName" className="text-slate-700">
                                        {formData.edition === 'personal' ? 'Your Name / Account Name' : 'Company Name'}
                                    </Label>
                                    <div className="relative">
                                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            id="companyName"
                                            name="companyName"
                                            value={formData.companyName}
                                            onChange={handleInputChange}
                                            placeholder={formData.edition === 'personal' ? 'Γιάννης Παπαδόπουλος' : 'Acme Logistics'}
                                            className="pl-10 h-12 bg-white border-slate-300"
                                            data-testid="register-company-name"
                                        />
                                    </div>
                                </div>

                                {/* Edition Selection */}
                                <div className="space-y-2">
                                    <Label className="text-slate-700">Select Edition</Label>
                                    <div className="grid grid-cols-1 gap-3">
                                        {/* Personal Edition */}
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, edition: 'personal', plan: 'basic' })}
                                            className={`p-4 rounded-xl border-2 text-left transition-all ${
                                                formData.edition === 'personal'
                                                    ? 'border-violet-500 bg-violet-500/5'
                                                    : 'border-slate-200 hover:border-slate-300'
                                            }`}
                                            data-testid="edition-select-personal"
                                        >
                                            <div className="flex items-center gap-2 mb-2">
                                                <UserCircle className={`w-5 h-5 ${formData.edition === 'personal' ? 'text-violet-600' : 'text-slate-400'}`} />
                                                <span className={`font-semibold ${formData.edition === 'personal' ? 'text-violet-600' : 'text-slate-700'}`}>
                                                    Personal
                                                </span>
                                                <span className="text-xs bg-violet-100 text-violet-600 px-2 py-0.5 rounded ml-auto">
                                                    Για Οικογένειες
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-500 mb-2">
                                                Για οικογένειες με έως 3 οχήματα & 3 χρήστες. Mobile-first.
                                            </p>
                                            <div className="flex items-center gap-2 text-xs text-violet-600 font-medium">
                                                <ParkingCircle className="w-4 h-4" />
                                                <span>€2.50/όχημα/μήνα</span>
                                            </div>
                                        </button>

                                        {/* Inspection & Carrier in a row */}
                                        <div className="grid grid-cols-2 gap-3">
                                            <button
                                                type="button"
                                                onClick={() => setFormData({ ...formData, edition: 'inspection', plan: 'basic' })}
                                                className={`p-4 rounded-xl border-2 text-left transition-all ${
                                                    formData.edition === 'inspection'
                                                        ? 'border-[#1B7EB5] bg-[#1B7EB5]/5'
                                                        : 'border-slate-200 hover:border-slate-300'
                                                }`}
                                                data-testid="edition-select-inspection"
                                            >
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Car className={`w-5 h-5 ${formData.edition === 'inspection' ? 'text-[#1B7EB5]' : 'text-slate-400'}`} />
                                                    <span className={`font-semibold ${formData.edition === 'inspection' ? 'text-[#1B7EB5]' : 'text-slate-700'}`}>
                                                        Inspection
                                                    </span>
                                                </div>
                                                <p className="text-xs text-slate-500">
                                                    Για επιβατικά, ταξί, ενοικιαζόμενα
                                                </p>
                                                <p className="text-xs text-[#1B7EB5] mt-1 font-medium">
                                                    από €4/όχημα/μήνα
                                                </p>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setFormData({ ...formData, edition: 'carrier', plan: 'basic' })}
                                                className={`p-4 rounded-xl border-2 text-left transition-all ${
                                                    formData.edition === 'carrier'
                                                        ? 'border-[#8CC774] bg-[#8CC774]/5'
                                                        : 'border-slate-200 hover:border-slate-300'
                                                }`}
                                                data-testid="edition-select-carrier"
                                            >
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Package className={`w-5 h-5 ${formData.edition === 'carrier' ? 'text-[#8CC774]' : 'text-slate-400'}`} />
                                                    <span className={`font-semibold ${formData.edition === 'carrier' ? 'text-[#8CC774]' : 'text-slate-700'}`}>
                                                        Carrier
                                                    </span>
                                                </div>
                                                <p className="text-xs text-slate-500">
                                                    Για μεταφορικές, logistics
                                                </p>
                                                <p className="text-xs text-[#8CC774] mt-1 font-medium">
                                                    από €5/όχημα/μήνα
                                                </p>
                                            </button>
                                        </div>

                                        {/* Hybrid Edition - full width */}
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, edition: 'hybrid', plan: 'basic' })}
                                            className={`p-4 rounded-xl border-2 text-left transition-all ${
                                                formData.edition === 'hybrid'
                                                    ? 'border-orange-500 bg-orange-500/5'
                                                    : 'border-slate-200 hover:border-slate-300'
                                            }`}
                                            data-testid="edition-select-hybrid"
                                        >
                                            <div className="flex items-center gap-2 mb-2">
                                                <Layers className={`w-5 h-5 ${formData.edition === 'hybrid' ? 'text-orange-500' : 'text-slate-400'}`} />
                                                <span className={`font-semibold ${formData.edition === 'hybrid' ? 'text-orange-500' : 'text-slate-700'}`}>
                                                    Hybrid
                                                </span>
                                                <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded ml-auto">
                                                    Μικτός Στόλος
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-500 mb-2">
                                                Για εταιρείες με επιβατικά & φορτηγά. Ιδανικό για πωλητές + παραδόσεις.
                                            </p>
                                            <div className="flex items-center gap-2 text-xs text-orange-500 font-medium">
                                                <Layers className="w-4 h-4" />
                                                <span>από €5/όχημα/μήνα - Όλες οι δυνατότητες</span>
                                            </div>
                                        </button>

                                        {/* Leasing Edition - full width */}
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, edition: 'leasing', plan: 'basic', leasingCompanyId: 'none' })}
                                            className={`p-4 rounded-xl border-2 text-left transition-all ${
                                                formData.edition === 'leasing'
                                                    ? 'border-amber-500 bg-amber-500/5'
                                                    : 'border-slate-200 hover:border-slate-300'
                                            }`}
                                            data-testid="edition-select-leasing"
                                        >
                                            <div className="flex items-center gap-2 mb-2">
                                                <Handshake className={`w-5 h-5 ${formData.edition === 'leasing' ? 'text-amber-600' : 'text-slate-400'}`} />
                                                <span className={`font-semibold ${formData.edition === 'leasing' ? 'text-amber-600' : 'text-slate-700'}`}>
                                                    Leasing
                                                </span>
                                                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded ml-auto">
                                                    Για εταιρείες Leasing
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-500">
                                                Αναθέστε οχήματα στους μισθωτές σας και παρακολουθήστε χιλιόμετρα & check-in/check-out.
                                            </p>
                                        </button>

                                        {/* Workshop Edition - full width */}
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, edition: 'workshop', plan: 'basic', leasingCompanyId: 'none' })}
                                            className={`p-4 rounded-xl border-2 text-left transition-all ${
                                                formData.edition === 'workshop'
                                                    ? 'border-teal-600 bg-teal-600/5'
                                                    : 'border-slate-200 hover:border-slate-300'
                                            }`}
                                            data-testid="edition-select-workshop"
                                        >
                                            <div className="flex items-center gap-2 mb-2">
                                                <Wrench className={`w-5 h-5 ${formData.edition === 'workshop' ? 'text-teal-700' : 'text-slate-400'}`} />
                                                <span className={`font-semibold ${formData.edition === 'workshop' ? 'text-teal-700' : 'text-slate-700'}`}>
                                                    Συνεργείο
                                                </span>
                                                <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded ml-auto">
                                                    Για συνεργεία
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-500">
                                                Ραντεβού από εταιρείες leasing, δικοί σας πελάτες, εβδομαδιαίο ημερολόγιο, χωρητικότητα και chat.
                                            </p>
                                        </button>
                                    </div>
                                </div>

                                {!['leasing', 'workshop'].includes(formData.edition) && leasingCompanies.length > 0 && (
                                    <div className="space-y-2" data-testid="register-leasing-section">
                                        <Label className="text-slate-700">Leasing (προαιρετικό)</Label>
                                        <Select value={formData.leasingCompanyId} onValueChange={(value) => setFormData({ ...formData, leasingCompanyId: value })}>
                                            <SelectTrigger className="h-12 bg-white border-slate-300" data-testid="register-leasing-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white border-slate-200">
                                                <SelectItem value="none">Δεν έχω όχημα leasing</SelectItem>
                                                {leasingCompanies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-slate-500">Αν έχετε όχημα από εταιρεία leasing, επιλέξτε την για αυτόματη σύνδεση.</p>
                                    </div>
                                )}
                            </div>

                            <Button 
                                onClick={handleNextStep}
                                className="w-full h-12 bg-[#1B7EB5] hover:bg-[#1B7EB5]/90 text-white"
                                data-testid="register-next-1"
                            >
                                Continue
                                <ChevronRight className="w-5 h-5 ml-2" />
                            </Button>

                            <p className="text-center text-sm text-slate-500">
                                Already have an account?{' '}
                                <Link to="/login" className="text-[#1B7EB5] hover:underline">
                                    Sign in
                                </Link>
                            </p>
                        </div>
                    )}

                    {/* Step 2: Personal Info */}
                    {step === 2 && (
                        <div className="space-y-6" data-testid="register-step-2">
                            <div>
                                <h1 className="text-2xl font-bold text-slate-800 mb-2">Your details</h1>
                                <p className="text-slate-500">You&apos;ll be the admin for {formData.companyName}</p>
                            </div>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name" className="text-slate-700">Your Name</Label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder="John Smith"
                                            className="pl-10 h-12 bg-white border-slate-300"
                                            data-testid="register-name"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-slate-700">Email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            placeholder="john@acmelogistics.com"
                                            className="pl-10 h-12 bg-white border-slate-300"
                                            data-testid="register-email"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password" className="text-slate-700">Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            id="password"
                                            name="password"
                                            type="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            placeholder="••••••••"
                                            className="pl-10 h-12 bg-white border-slate-300"
                                            data-testid="register-password"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            type="password"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            placeholder="••••••••"
                                            className="pl-10 h-12 bg-white border-slate-300"
                                            data-testid="register-confirm-password"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <Button 
                                    variant="outline"
                                    onClick={() => setStep(1)}
                                    className="flex-1 h-12 border-slate-300"
                                >
                                    Back
                                </Button>
                                <Button 
                                    onClick={handleNextStep}
                                    className="flex-1 h-12 bg-[#1B7EB5] hover:bg-[#1B7EB5]/90 text-white"
                                    data-testid="register-next-2"
                                >
                                    Continue
                                    <ChevronRight className="w-5 h-5 ml-2" />
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Plan Selection */}
                    {step === 3 && (
                        <div className="space-y-6" data-testid="register-step-3">
                            <div>
                                <h1 className="text-2xl font-bold text-slate-800 mb-2">
                                    {formData.edition === 'personal' ? 'Επιβεβαίωση' : 'Choose your plan'}
                                </h1>
                                <p className="text-slate-500">Start with a 14-day free trial. No credit card required.</p>
                            </div>

                            <div className="space-y-4">
                                {/* Plan Selection - Only show for business editions */}
                                {!['personal', 'workshop'].includes(formData.edition) && (
                                    <div className="space-y-2">
                                        <Label className="text-slate-700">Select Plan</Label>
                                        <Select value={formData.plan} onValueChange={(value) => setFormData({...formData, plan: value})}>
                                            <SelectTrigger className="h-12 bg-white border-slate-300" data-testid="register-plan-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white border-slate-200">
                                                {plans.map((plan) => (
                                                    <SelectItem key={plan.value} value={plan.value}>
                                                        <div className="flex items-center gap-2">
                                                            <span>{plan.label}</span>
                                                            {plan.pricePerVehicle && (
                                                                <span className="text-xs text-slate-500">
                                                                    (€{plan.pricePerVehicle}/vehicle, up to {plan.vehicleLimit})
                                                                </span>
                                                            )}
                                                            {plan.recommended && (
                                                                <span className="text-xs bg-[#1B7EB5]/10 text-[#1B7EB5] px-2 py-0.5 rounded">
                                                                    Recommended
                                                                </span>
                                                            )}
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                {formData.edition === 'workshop' && (
                                    <div className="rounded-lg p-4 border bg-teal-600/5 border-teal-600/20 space-y-1 text-sm text-slate-600" data-testid="register-workshop-plan">
                                        <div className="flex items-center gap-2 mb-2"><Wrench className="w-5 h-5 text-teal-700" /><span className="font-semibold text-slate-800">Cofleet Συνεργείο</span></div>
                                        {['Ραντεβού συντήρησης από εταιρείες leasing', 'Δικοί σας πελάτες και ραντεβού', 'Εβδομαδιαίο ημερολόγιο & χωρητικότητα', 'Chat με υπευθύνους στόλου'].map((f) => (
                                            <div key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-teal-700" /><span>{f}</span></div>
                                        ))}
                                    </div>
                                )}

                                {/* Plan Details */}
                                {selectedPlan && formData.edition !== 'workshop' && (
                                    <div className={`rounded-lg p-4 border ${
                                        formData.edition === 'personal' 
                                            ? 'bg-violet-500/5 border-violet-500/20' 
                                            : 'bg-[#1B7EB5]/5 border-[#1B7EB5]/20'
                                    }`}>
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                {formData.edition === 'personal' && <ParkingCircle className="w-5 h-5 text-violet-600" />}
                                                <span className="font-semibold text-slate-800">
                                                    {formData.edition === 'personal' ? 'Personal Edition' : `${selectedPlan.label} Plan`}
                                                </span>
                                            </div>
                                            {selectedPlan.pricePerVehicle ? (
                                                <span className={`text-lg font-bold ${formData.edition === 'personal' ? 'text-violet-600' : 'text-[#1B7EB5]'}`}>
                                                    €{selectedPlan.pricePerVehicle}/vehicle/mo
                                                </span>
                                            ) : (
                                                <span className="text-sm text-slate-500">Contact Sales</span>
                                            )}
                                        </div>
                                        <div className="space-y-1 text-sm text-slate-600">
                                            {formData.edition === 'personal' ? (
                                                <>
                                                    <div className="flex items-center gap-2">
                                                        <Check className="w-4 h-4 text-violet-600" />
                                                        <span>Έως 3 χρήστες (οικογένεια)</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Check className="w-4 h-4 text-violet-600" />
                                                        <span>Έως 3 οχήματα</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Check className="w-4 h-4 text-violet-600" />
                                                        <span>Mobile-first εφαρμογή</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Check className="w-4 h-4 text-violet-600" />
                                                        <span>Χάρτης Στάθμευσης & Parking Safety</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Check className="w-4 h-4 text-violet-600" />
                                                        <span>AI Ανίχνευση Ζημιών</span>
                                                    </div>
                                                </>
                                            ) : selectedPlan.vehicleLimit ? (
                                                <>
                                                    <div className="flex items-center gap-2">
                                                        <Check className="w-4 h-4 text-[#8CC774]" />
                                                        <span>Up to {selectedPlan.vehicleLimit} vehicles</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Check className="w-4 h-4 text-[#8CC774]" />
                                                        <span>Unlimited drivers</span>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="flex items-center gap-2">
                                                    <Check className="w-4 h-4 text-[#8CC774]" />
                                                    <span>Unlimited vehicles & drivers</span>
                                                </div>
                                            )}
                                            {formData.edition !== 'personal' && (
                                                <div className="flex items-center gap-2">
                                                    <Check className="w-4 h-4 text-[#8CC774]" />
                                                    <span>All features included</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Summary */}
                                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500">Company</span>
                                        <span className="text-slate-800 font-medium">{formData.companyName}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500">Admin</span>
                                        <span className="text-slate-800 font-medium">{formData.email}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500">Plan</span>
                                        <span className="text-slate-800 font-medium">{selectedPlan?.label}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500">Trial Period</span>
                                        <span className="text-[#8CC774] font-medium">14 days free</span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <Button 
                                    variant="outline"
                                    onClick={() => setStep(2)}
                                    className="flex-1 h-12 border-slate-300"
                                >
                                    Back
                                </Button>
                                <Button 
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    className="flex-1 h-12 bg-[#1B7EB5] hover:bg-[#1B7EB5]/90 text-white"
                                    data-testid="register-submit"
                                >
                                    {loading ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <>
                                            Start Free Trial
                                            <ChevronRight className="w-5 h-5 ml-2" />
                                        </>
                                    )}
                                </Button>
                            </div>

                            <p className="text-center text-xs text-slate-500">
                                By creating an account, you agree to our Terms of Service and Privacy Policy.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Right Side - Illustration (Desktop only) */}
            <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[#1B7EB5] to-[#58A6B4] items-center justify-center p-12">
                <div className="text-center text-white max-w-md">
                    <h2 className="text-3xl font-bold mb-4">Welcome to Cofleet</h2>
                    <p className="text-white/80 mb-8">
                        Join hundreds of companies managing their fleets smarter with AI-powered inspections, 
                        maintenance scheduling, and real-time analytics.
                    </p>
                    <div className="grid grid-cols-2 gap-6 text-left">
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                            <div className="text-2xl font-bold">500+</div>
                            <div className="text-sm text-white/70">Companies</div>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                            <div className="text-2xl font-bold">10,000+</div>
                            <div className="text-sm text-white/70">Vehicles Managed</div>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                            <div className="text-2xl font-bold">99.9%</div>
                            <div className="text-sm text-white/70">Uptime</div>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                            <div className="text-2xl font-bold">24/7</div>
                            <div className="text-sm text-white/70">Support</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
